import { create } from 'zustand';
import { Business, MapBounds, ActivityForHomepage, MapOperationState, MapStatus, FilterPerformance, FilterMetadata } from '@/types/homepage';
import { fetchAllBusinesses, fetchBusinessActivities } from '@/utils/homepageApi';
import { toast } from '@/hooks/use-toast';
import { log } from '@/utils/logger';

// NEW: Import enhanced filtering utilities (optional)
import { enhancedFilterBusinesses, clearFilterCache } from '@/utils/filterEnhancement';

// NEW: Import comprehensive filter logger
import { filterLogger } from '@/services/filterLogger';

// Constants used for map operation thresholds
const EXPLORATION_THRESHOLD = 0.1; // Threshold for switching from search mode to exploration mode (about 10km)
const DEBOUNCE_DELAY = 500; // Debounce delay for map pan/zoom events

// Activity filters
interface ActivityFilters {
  showAnimalActivities: boolean;
  showAdventureActivities: boolean;
}

// NEW: Seasonal filters
interface SeasonalFilters {
  selectedSeasons: string[];
}

// NEW: Additional filters for comprehensive filtering
interface AdditionalFilters {
  priceRange: [number, number];
  duration: [number, number];
  groupSize: [number, number];
  hasWiFi: boolean;
  familyFriendly: boolean;
  offersMeals: boolean;
  isTourGuided: boolean;
  accessibilityFeatures: boolean;
  selectedSeason: string | null; // Quick seasonal filter
}

// Define the store state interface
interface HomePageState {
  // Businesses data
  businesses: Business[];
  filteredBusinesses: Business[];
  businessActivities: Record<string, ActivityForHomepage[]>;
  selectedBusiness: Business | null;
  
  // Map state
  mapRef: any;
  currentBounds: MapBounds | null;
  mapStatus: MapStatus;
  
  // Search and filters
  searchQuery: string;
  originalSearchQuery: string; // Store original search for reference
  isExplorationMode: boolean; // Track if user is exploring vs searching
  isLocationSearch: boolean; // NEW: Track if current search is location-based (like "spain") vs text-based (like "bear sanctuary")
  activityFilters: ActivityFilters;
  selectedAnimalTypes: string[];
  selectedAdventureTypes: string[]; // NEW: Adventure types filter
  activeExperienceFilter: string | null;
  
  // NEW: Enhanced filter state
  seasonalFilters: SeasonalFilters;
  additionalFilters: AdditionalFilters;
  
  // NEW: Enhanced filtering state (optional)
  useEnhancedFiltering: boolean; // Toggle for enhanced filtering
  filterPerformance: FilterPerformance | null; // Performance metrics
  filterMetadata: FilterMetadata | null; // Filter metadata
  
  // NEW: Debounced filter state
  isFilterUpdatePending: boolean;
  pendingFilterUpdates: Map<string, () => void>;
  filterUpdateTimeout: NodeJS.Timeout | null;
  
  // Loading state
  isLoading: boolean;
  
  // UI state
  isMapFullscreen: boolean;
  drawerHeight: string;
}

// Define the store actions interface
interface HomePageActions {
  // Map operations
  setMapRef: (ref: any) => void;
  loadBusinessesForBounds: (bounds: MapBounds, isFromSearch?: boolean) => Promise<Business[]>;
  handleMapMove: (bounds: MapBounds, isFlying?: boolean) => Promise<void>;
  handleMapSearch: (bounds: MapBounds) => Promise<Business[]>;
  setFlyingState: (isFlying: boolean) => void;
  
  // Business operations
  setBusinesses: (businesses: Business[]) => void;
  handleBusinessSelect: (business: Business | null) => void;
  loadBusinessActivities: (businessId: string) => Promise<void>;
  
  // Filter operations
  setSearchQuery: (query: string) => void;
  setOriginalSearchQuery: (query: string) => void;
  setExplorationMode: (isExploring: boolean) => void;
  setLocationSearch: (isLocationSearch: boolean) => void; // NEW: Set location search mode
  clearSearchWhenExploring: (currentBounds: MapBounds) => void;
  setActivityFilters: (filters: ActivityFilters) => void;
  setSelectedAnimalTypes: (types: string[]) => void;
  setSelectedAdventureTypes: (types: string[]) => void;
  setActiveExperienceFilter: (filter: string | null) => void;
  
  // NEW: Debounced filter operations (for performance optimization)
  setSearchQueryDebounced: (query: string) => void;
  setActivityFiltersDebounced: (filters: ActivityFilters) => void;
  setSelectedAnimalTypesDebounced: (types: string[]) => void;
  setSelectedAdventureTypesDebounced: (types: string[]) => void;
  setActiveExperienceFilterDebounced: (filter: string | null) => void;
  setAdditionalFiltersDebounced: (filters: AdditionalFilters) => void;
  setSelectedSeasonDebounced: (season: string | null) => void;
  
  // Filter update control
  scheduleFilterUpdate: (filterId?: string) => void;
  cancelPendingFilterUpdates: () => void;
  isFilterUpdatePending: boolean;
  
  // NEW: Seasonal and additional filter operations
  setSeasonalFilters: (filters: SeasonalFilters) => void;
  setAdditionalFilters: (filters: AdditionalFilters) => void;
  setSelectedSeason: (season: string | null) => void;
  clearAllFilters: () => void;
  
  // NEW: Enhanced filtering operations (optional)
  setUseEnhancedFiltering: (enabled: boolean) => void;
  clearFilterCache: () => void;
  getFilterStatistics: () => { size: number; hitRate?: number };
  
  // UI operations
  setMapFullscreen: (isFullscreen: boolean) => void;
  setDrawerHeight: (height: string) => void;
  setLoading: (isLoading: boolean) => void;
  
  // Map status operations
  updateMapStatus: (updates: Partial<MapStatus>) => void;
  setMapStatus: (status: MapStatus) => void;
  
  // Computed business filtering
  updateFilteredBusinesses: () => void;
  
  // Clear bounds cache (useful for testing or manual reset)
  clearBoundsCache: () => void;
}

// Module-level variables for map interaction tracking (TODO P2-B: move to refs)
let pendingBoundsChange: MapBounds | null = null;
let allBusinessesRequest: Promise<Business[]> | null = null;
let debounceTimeout: NodeJS.Timeout | null = null;
let searchLocationBounds: MapBounds | null = null;

// Loads the catalogue once per visit. A failed load is retried on the next map move.
const loadAllBusinesses = (): Promise<Business[]> => {
  if (!allBusinessesRequest) {
    allBusinessesRequest = fetchAllBusinesses().catch(error => {
      allBusinessesRequest = null;
      throw error;
    });
  }
  return allBusinessesRequest;
};

// Whether an operator sits inside a map view. Views can pass the 180th meridian when zoomed out.
export const isBusinessInBounds = (business: Business, bounds: MapBounds): boolean => {
  const [lng, lat] = business.location.coordinates.coordinates;
  const { northEast, southWest } = bounds;
  if (lat < southWest.latitude || lat > northEast.latitude) return false;
  if (northEast.longitude - southWest.longitude >= 360) return true;
  return [lng, lng - 360, lng + 360].some(x => x >= southWest.longitude && x <= northEast.longitude);
};

// Utility function to detect if user is exploring away from search location (like Airbnb/Google Maps)
const isExploringAwayFromSearch = (currentBounds: MapBounds, originalSearchBounds: MapBounds | null): boolean => {
  if (!originalSearchBounds) return false;
  
  const threshold = EXPLORATION_THRESHOLD;
  const latDiff = Math.abs(currentBounds.northEast.latitude - originalSearchBounds.northEast.latitude) +
                  Math.abs(currentBounds.southWest.latitude - originalSearchBounds.southWest.latitude);
  const lngDiff = Math.abs(currentBounds.northEast.longitude - originalSearchBounds.northEast.longitude) +
                  Math.abs(currentBounds.southWest.longitude - originalSearchBounds.southWest.longitude);
  
  return latDiff > threshold || lngDiff > threshold;
};

// ENHANCED: Utility function to compare business arrays for content equality
const areBusinessArraysEqual = (arr1: Business[], arr2: Business[]): boolean => {
  if (arr1.length !== arr2.length) return false;
  
  // Create sets of business IDs for fast comparison
  const ids1 = new Set(arr1.map(b => b._id));
  const ids2 = new Set(arr2.map(b => b._id));
  
  // Check if all IDs are the same
  if (ids1.size !== ids2.size) return false;
  
  for (const id of ids1) {
    if (!ids2.has(id)) return false;
  }
  
  return true;
};

// ENHANCED: Function to reuse existing business objects when possible
const mergeBusinessArrays = (newBusinesses: Business[], existingBusinesses: Business[]): Business[] => {
  if (existingBusinesses.length === 0) return newBusinesses;
  
  // Create a map of existing businesses for fast lookup
  const existingMap = new Map(existingBusinesses.map(b => [b._id, b]));
  
  // Reuse existing business objects where IDs match, add new ones
  const mergedBusinesses = newBusinesses.map(newBusiness => {
    const existing = existingMap.get(newBusiness._id);
    // Reuse existing object if it exists (maintains reference equality for React)
    return existing || newBusiness;
  });
  
  return mergedBusinesses;
};

// 🚀 PREDICTION FUNCTION: Determine if businesses are likely unchanged to prevent loading cycle
// 🚀 KISS: No complex prediction needed - simple business comparison after API call works perfectly

// Get the center point of bounds for distance calculations
const getBoundsCenter = (bounds: MapBounds) => ({
  lat: (bounds.northEast.latitude + bounds.southWest.latitude) / 2,
  lng: (bounds.northEast.longitude + bounds.southWest.longitude) / 2
});

// Create the store
const useHomePageStore = create<HomePageState & HomePageActions>((set, get) => ({
  // Initial state
  businesses: [],
  filteredBusinesses: [],
  businessActivities: {},
  selectedBusiness: null,
  
  mapRef: { current: null },
  currentBounds: null,
  mapStatus: {
    state: 'idle' as MapOperationState,
    initialLoadCompleted: false,
    lastLoadTime: Date.now(),
    userHasInteracted: false,
    businessesStable: false
  },
  
  searchQuery: '',
  originalSearchQuery: '',
  isExplorationMode: false,
  isLocationSearch: false,
  activityFilters: {
    showAnimalActivities: true,
    showAdventureActivities: true
  },
  selectedAnimalTypes: [],
  selectedAdventureTypes: [],
  activeExperienceFilter: null,
  
  // NEW: Enhanced filter state
  seasonalFilters: {
    selectedSeasons: []
  },
  additionalFilters: {
    priceRange: [0, 2000], // Default price range $0-$2000
    duration: [0, 14],     // Default duration 0-14 days
    groupSize: [1, 20],    // Default group size 1-20 people
    hasWiFi: false,
    familyFriendly: false,
    offersMeals: false,
    isTourGuided: false,
    accessibilityFeatures: false,
    selectedSeason: null
  },
  
  // NEW: Enhanced filtering state (optional)
  useEnhancedFiltering: false,
  filterPerformance: null,
  filterMetadata: null,
  
  isLoading: false,
  
  isMapFullscreen: false,
  drawerHeight: '40%',
  
  // NEW: Debounced filter state
  isFilterUpdatePending: false,
  pendingFilterUpdates: new Map(),
  filterUpdateTimeout: null,
  
  // Actions
  setMapRef: (instance) => {
    set(state => ({
      mapRef: { ...state.mapRef, current: instance }
    }));
  },

  loadBusinessesForBounds: async (bounds, isFromSearch = false) => {
    const isFirstLoad = !get().mapStatus.businessesStable;
    if (isFirstLoad) {
      set(state => ({
        mapStatus: { ...state.mapStatus, state: isFromSearch ? 'searching' : 'loading' },
        isLoading: true
      }));
    }

    try {
      const allBusinesses = await loadAllBusinesses();
      if (isFirstLoad) {
        set({ businesses: allBusinesses });
        get().updateFilteredBusinesses();
      }

      set(state => ({
        currentBounds: bounds,
        isLoading: false,
        mapStatus: {
          ...state.mapStatus,
          state: 'idle',
          lastLoadTime: Date.now(),
          initialLoadCompleted: true,
          businessesStable: true
        }
      }));
    } catch (error) {
      console.error('[HomePageStore] Error loading businesses:', error);
      set(state => ({
        currentBounds: bounds,
        isLoading: false,
        mapStatus: { ...state.mapStatus, state: 'idle' }
      }));
    }

    return get().businesses;
  },
  
  handleMapMove: async (bounds, isFlying) => {
    const { mapStatus, searchQuery, isExplorationMode } = get();

    if (isFlying || mapStatus.state === 'flying' || mapStatus.state === 'searching') {
      return;
    }

    if (mapStatus.state === 'loading') {
      pendingBoundsChange = bounds;
      return;
    }

    if (!mapStatus.userHasInteracted) {
      set(state => ({
        mapStatus: { ...state.mapStatus, userHasInteracted: true }
      }));
    }

    if (searchQuery && !isExplorationMode && searchLocationBounds &&
        isExploringAwayFromSearch(bounds, searchLocationBounds)) {
      set(state => ({
        isExplorationMode: true,
        searchQuery: '',
        originalSearchQuery: state.searchQuery
      }));
    }

    pendingBoundsChange = bounds;

    if (debounceTimeout) clearTimeout(debounceTimeout);

    return new Promise<void>((resolve) => {
      debounceTimeout = setTimeout(async () => {
        const currentState = get();
        const canLoad = currentState.mapStatus.state === 'idle' || currentState.mapStatus.state === 'ready';
        if (canLoad && pendingBoundsChange) {
          const boundsToFetch = { ...pendingBoundsChange };
          pendingBoundsChange = null;
          try {
            await get().loadBusinessesForBounds(boundsToFetch, false);
          } catch (error) {
            console.error('[HomePageStore] Error in debounced map move:', error);
          }
        }
        resolve();
      }, DEBOUNCE_DELAY);
    });
  },
  
  handleMapSearch: async (bounds) => {
    try {
      if (!searchLocationBounds) {
        searchLocationBounds = { ...bounds };
      }
      set({ isLocationSearch: true });

      const result = await get().loadBusinessesForBounds(bounds, true);
      get().updateFilteredBusinesses();
      return result;
    } catch (error) {
      console.error('[HomePageStore] Error during map search:', error);
      return [];
    }
  },
  
  setFlyingState: (isFlying: boolean) => {
    set((state) => ({
      mapStatus: {
        ...state.mapStatus,
        state: isFlying ? 'flying' : 'idle',
      },
    }));
  },
  
  setBusinesses: (businesses) => {
    set({ businesses });
    get().updateFilteredBusinesses();
  },
  
  handleBusinessSelect: async (business) => {
    set({ selectedBusiness: business });
    if (business) {
      await get().loadBusinessActivities(business._id);
    }
  },
  
  loadBusinessActivities: async (businessId) => {
    const { businessActivities } = get();
    
    // Skip if already loaded
    if (businessActivities[businessId] && businessActivities[businessId].length > 0) {
      return;
    }
    
    try {
      const activities = await fetchBusinessActivities(businessId);
      set(state => ({
        businessActivities: {
          ...state.businessActivities,
          [businessId]: activities
        }
      }));
    } catch (error) {
      console.error(`[HomePageStore] Error loading activities for business ${businessId}:`, error);
    }
  },
  
  setSearchQuery: (searchQuery) => {
    set({
      searchQuery,
      isExplorationMode: false,
      isLocationSearch: false,
      originalSearchQuery: searchQuery
    });
    if (searchQuery.trim()) {
      searchLocationBounds = null;
    }
    get().updateFilteredBusinesses();
  },
  
  setOriginalSearchQuery: (query: string) => {
    set({ originalSearchQuery: query });
    get().updateFilteredBusinesses();
  },
  
  setExplorationMode: (isExploring: boolean) => {
    set({ isExplorationMode: isExploring });
    get().updateFilteredBusinesses();
  },
  
  setLocationSearch: (isLocationSearch: boolean) => {
    set({ isLocationSearch });
  },
  
  clearSearchWhenExploring: (currentBounds: MapBounds) => {
    const { isExplorationMode } = get();
    if (isExplorationMode && currentBounds) {
      set({ searchQuery: '' });
      get().updateFilteredBusinesses();
    }
  },
  
  setActivityFilters: (activityFilters) => {
    set({ activityFilters });
    get().updateFilteredBusinesses();
  },
  
  setSelectedAnimalTypes: (selectedAnimalTypes) => {
    set({ selectedAnimalTypes });
    get().updateFilteredBusinesses();
  },
  
  setSelectedAdventureTypes: (selectedAdventureTypes) => {
    set({ selectedAdventureTypes });
    get().updateFilteredBusinesses();
  },
  
  setActiveExperienceFilter: (activeExperienceFilter) => {
    set({ activeExperienceFilter });
    get().updateFilteredBusinesses();
  },
  
  setMapFullscreen: (isMapFullscreen) => set({ isMapFullscreen }),
  
  setDrawerHeight: (drawerHeight) => set({ drawerHeight }),
  
  setLoading: (isLoading) => set({ isLoading }),
  
  updateMapStatus: (updates) => set((state) => ({
    mapStatus: { ...state.mapStatus, ...updates }
  })),
  
  setMapStatus: (status) => set({ mapStatus: status }),
  
  clearBoundsCache: () => {
    pendingBoundsChange = null;
    searchLocationBounds = null;
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
      debounceTimeout = null;
    }
    set(() => ({
      isExplorationMode: false,
      searchQuery: '',
      originalSearchQuery: ''
    }));
  },
  
  updateFilteredBusinesses: () => {
    const {
      businesses,
      searchQuery,
      isExplorationMode,
      activityFilters,
      selectedAnimalTypes,
      selectedAdventureTypes,
      activeExperienceFilter,
      businessActivities,
      useEnhancedFiltering,
    } = get();

    if (useEnhancedFiltering) {
      try {
        const result = enhancedFilterBusinesses(businesses, businessActivities, {
          searchQuery,
          isExplorationMode,
          isLocationSearch: get().isLocationSearch,
          activityFilters,
          selectedAnimalTypes,
          selectedAdventureTypes,
          activeExperienceFilter
        });
        set({
          filteredBusinesses: result.filteredBusinesses,
          filterPerformance: result.performance,
          filterMetadata: result.metadata
        });
        return;
      } catch (error) {
        console.error('[HomePageStore] Enhanced filtering failed, falling back to legacy:', error);
      }
    }

    let filtered = businesses;

    if (!(activityFilters.showAnimalActivities && activityFilters.showAdventureActivities)) {
      filtered = filtered.filter(business => {
        if (activityFilters.showAnimalActivities && business.hasAnimalActivities) return true;
        if (activityFilters.showAdventureActivities && business.hasAdventureActivities) return true;
        return false;
      });
    }

    if (searchQuery.trim() && !isExplorationMode && !get().isLocationSearch) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(business =>
        business.name.toLowerCase().includes(query) ||
        business.description.toLowerCase().includes(query) ||
        business.location.address.city.toLowerCase().includes(query) ||
        business.location.address.country.toLowerCase().includes(query)
      );
    }

    if (activeExperienceFilter) {
      filtered = filtered.filter(business => {
        const activities = businessActivities[business._id] || [];
        return activities.some(activity =>
          activity.name.toLowerCase().includes(activeExperienceFilter.toLowerCase()) ||
          activity.activityType?.toLowerCase().includes(activeExperienceFilter.toLowerCase())
        );
      });
    }

    if (selectedAnimalTypes.length > 0) {
      filtered = filtered.filter(business => {
        const activities = businessActivities[business._id] || [];
        return selectedAnimalTypes.some(animalType =>
          activities.some(activity => activity.name.toLowerCase().includes(animalType.toLowerCase()))
        );
      });
    }

    if (selectedAdventureTypes.length > 0) {
      filtered = filtered.filter(business => {
        const activities = businessActivities[business._id] || [];
        return selectedAdventureTypes.some(adventureType =>
          activities.some(activity => activity.name.toLowerCase().includes(adventureType.toLowerCase()))
        );
      });
    }

    const currentFiltered = get().filteredBusinesses;
    if (!areBusinessArraysEqual(filtered, currentFiltered)) {
      set({ filteredBusinesses: filtered });
    }
  },

  setUseEnhancedFiltering: (enabled: boolean) => {
    set({ useEnhancedFiltering: enabled });
    get().updateFilteredBusinesses();
  },
  
  clearFilterCache: () => {
    clearFilterCache();
    filterLogger.clearLogs();
    set({ useEnhancedFiltering: false, filterPerformance: null, filterMetadata: null });
    get().updateFilteredBusinesses();
  },
  
  getFilterStatistics: () => {
    const { useEnhancedFiltering, filterPerformance, filterMetadata, businesses, filteredBusinesses } = get();
    
    // Get analytics from filter logger
    const analytics = filterLogger.getAnalytics();
    
    const stats = {
      size: businesses.length,
      hitRate: filterPerformance?.cacheHit ? 100 : 0,
      enhanced: {
        enabled: useEnhancedFiltering,
        performance: filterPerformance,
        metadata: filterMetadata,
        analytics: analytics
      },
      current: {
        totalBusinesses: businesses.length,
        filteredBusinesses: filteredBusinesses.length,
        reductionPercentage: businesses.length > 0 ? 
          ((businesses.length - filteredBusinesses.length) / businesses.length * 100).toFixed(1) : '0'
      }
    };
    
    return stats;
  },

  // NEW: Seasonal and additional filter operations
  setSeasonalFilters: (filters: SeasonalFilters) => {
    set({ seasonalFilters: filters });
  },
  setAdditionalFilters: (filters: AdditionalFilters) => {
    set({ additionalFilters: filters });
  },
  setSelectedSeason: (season: string | null) => {
    set({ additionalFilters: { ...get().additionalFilters, selectedSeason: season } });
  },
  clearAllFilters: () => {
    set({
      searchQuery: '',
      originalSearchQuery: '',
      isExplorationMode: false,
      isLocationSearch: false,
      activityFilters: {
        showAnimalActivities: true,
        showAdventureActivities: true
      },
      selectedAnimalTypes: [],
      selectedAdventureTypes: [],
      activeExperienceFilter: null,
      seasonalFilters: { selectedSeasons: [] },
      additionalFilters: {
        priceRange: [0, 2000], // Default price range $0-$2000
        duration: [0, 14],     // Default duration 0-14 days
        groupSize: [1, 20],    // Default group size 1-20 people
        hasWiFi: false,
        familyFriendly: false,
        offersMeals: false,
        isTourGuided: false,
        accessibilityFeatures: false,
        selectedSeason: null
      },
      useEnhancedFiltering: false,
      filterPerformance: null,
      filterMetadata: null,
      isLoading: false,
      isMapFullscreen: false,
      drawerHeight: '40%'
    });
    get().updateFilteredBusinesses();
  },

  // NEW: Debounced filter operations (for performance optimization)
  setSearchQueryDebounced: (query: string) => {
    const logger = log.component('HomePageStore');
    logger.debug('Debounced search query update', { query });
    
    // Update search query immediately for UI feedback
    set({ searchQuery: query });
    
    // Schedule debounced filter update
    const filterId = 'searchQuery';
    get().scheduleFilterUpdate(filterId);
  },
  
  setActivityFiltersDebounced: (filters: ActivityFilters) => {
    const logger = log.component('HomePageStore');
    logger.debug('Debounced activity filters update', filters);
    
    // Update filters immediately for UI feedback
    set({ activityFilters: filters });
    
    // Schedule debounced filter update
    const filterId = 'activityFilters';
    get().scheduleFilterUpdate(filterId);
  },
  
  setSelectedAnimalTypesDebounced: (types: string[]) => {
    const logger = log.component('HomePageStore');
    logger.debug('Debounced animal types update', { types });
    
    // Update types immediately for UI feedback
    set({ selectedAnimalTypes: types });
    
    // Schedule debounced filter update
    const filterId = 'animalTypes';
    get().scheduleFilterUpdate(filterId);
  },
  
  setSelectedAdventureTypesDebounced: (types: string[]) => {
    const logger = log.component('HomePageStore');
    logger.debug('Debounced adventure types update', { types });
    
    // Update types immediately for UI feedback
    set({ selectedAdventureTypes: types });
    
    // Schedule debounced filter update
    const filterId = 'adventureTypes';
    get().scheduleFilterUpdate(filterId);
  },
  
  setActiveExperienceFilterDebounced: (filter: string | null) => {
    const logger = log.component('HomePageStore');
    logger.debug('Debounced experience filter update', { filter });
    
    // Update filter immediately for UI feedback
    set({ activeExperienceFilter: filter });
    
    // Schedule debounced filter update
    const filterId = 'experienceFilter';
    get().scheduleFilterUpdate(filterId);
  },
  
  setAdditionalFiltersDebounced: (filters: AdditionalFilters) => {
    const logger = log.component('HomePageStore');
    logger.debug('Debounced additional filters update', filters);
    
    // Update filters immediately for UI feedback
    set({ additionalFilters: filters });
    
    // Schedule debounced filter update
    const filterId = 'additionalFilters';
    get().scheduleFilterUpdate(filterId);
  },
  
  setSelectedSeasonDebounced: (season: string | null) => {
    const logger = log.component('HomePageStore');
    logger.debug('Debounced season update', { season });
    
    // Update season immediately for UI feedback
    set({ 
      additionalFilters: { 
        ...get().additionalFilters, 
        selectedSeason: season 
      } 
    });
    
    // Schedule debounced filter update
    const filterId = 'season';
    get().scheduleFilterUpdate(filterId);
  },

  // Filter update control
  scheduleFilterUpdate: (filterId: string = 'default') => {
    const logger = log.component('HomePageStore');
    const { pendingFilterUpdates, filterUpdateTimeout } = get();
    
    // Add this filter update to pending operations
    const newPendingUpdates = new Map(pendingFilterUpdates);
    newPendingUpdates.set(filterId, () => {
      logger.debug('Executing scheduled filter update', { filterId });
      get().updateFilteredBusinesses();
    });
    
    // Cancel existing timeout
    if (filterUpdateTimeout) {
      clearTimeout(filterUpdateTimeout);
    }
    
    // Set up new timeout
    const newTimeout = setTimeout(() => {
      const operations = Array.from(newPendingUpdates.values());
      logger.info('Executing batched filter updates', { 
        operationCount: operations.length,
        filterIds: Array.from(newPendingUpdates.keys())
      });
      
      // Execute all pending operations
      operations.forEach(operation => operation());
      
      // Clear state
      set({ 
        pendingFilterUpdates: new Map(),
        filterUpdateTimeout: null,
        isFilterUpdatePending: false
      });
    }, 300); // 300ms debounce delay
    
    // Update state
    set({ 
      pendingFilterUpdates: newPendingUpdates,
      filterUpdateTimeout: newTimeout,
      isFilterUpdatePending: true
    });
    
    logger.debug('Filter update scheduled', { 
      filterId, 
      totalPending: newPendingUpdates.size 
    });
  },
  
  cancelPendingFilterUpdates: () => {
    const logger = log.component('HomePageStore');
    const { filterUpdateTimeout } = get();
    
    if (filterUpdateTimeout) {
      clearTimeout(filterUpdateTimeout);
    }
    
    set({ 
      pendingFilterUpdates: new Map(),
      filterUpdateTimeout: null,
      isFilterUpdatePending: false
    });
    
    logger.debug('All pending filter updates cancelled');
  }
}));

export default useHomePageStore; 